import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth as useOIDCAuth } from 'react-oidc-context';
import { useLogin } from '../../hooks/useLogin';
import EmailVerificationPrompt from './EmailVerificationPrompt';

interface LoginFormProps {
  onForgotPassword?: () => void;
  onRegister?: () => void;
  onCompanyLogin?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onForgotPassword,
  onRegister,
  onCompanyLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    authError,
    errorDetails,
    handleFieldFocus,
    watchedValues,
  } = useLogin();

  const oidcAuth = useOIDCAuth();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleGoogleLogin = () => {
    console.log('🔐 [LOGIN FORM] Initiating Google login...');
    oidcAuth.signinRedirect();
  };

  // Handle EMAIL_NOT_VERIFIED error
  useEffect(() => {
    if (errorDetails?.code === 'EMAIL_NOT_VERIFIED' && errorDetails.email) {
      setUnverifiedEmail(errorDetails.email);
      setShowEmailVerification(true);
    }
  }, [errorDetails]);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        {/* Error Alert */}
        {authError && errorDetails?.code !== 'EMAIL_NOT_VERIFIED' && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              authError.includes('企業として登録') ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onCompanyLogin}
                  sx={{ fontWeight: 'bold' }}
                >
                  企業ログインへ
                </Button>
              ) : undefined
            }
          >
            {authError.includes('企業として登録')
              ? 'このメールアドレスは企業として登録されています。企業ログインをご利用ください。'
              : authError}
          </Alert>
        )}

        {/* Email Field */}
        <TextField
          {...register('email')}
          fullWidth
          label="メールアドレス"
          type="email"
          autoComplete="email"
          margin="normal"
          error={!!errors.email}
          helperText={errors.email?.message}
          onFocus={() => handleFieldFocus('email')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Password Field */}
        <TextField
          {...register('password')}
          fullWidth
          label="パスワード"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          margin="normal"
          error={!!errors.password}
          helperText={errors.password?.message}
          onFocus={() => handleFieldFocus('password')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Remember Me Checkbox */}
        <FormControlLabel
          control={<Checkbox {...register('rememberMe')} color="primary" />}
          label="ログイン状態を保持する"
          sx={{ mb: 2, alignSelf: 'flex-start' }}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting}
          sx={{
            mb: 2,
            py: 1.5,
            position: 'relative',
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  marginLeft: '-10px',
                }}
              />
              ログイン中...
            </>
          ) : (
            'ログイン'
          )}
        </Button>

        {/* Google Login Button */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleGoogleLogin}
          startIcon={<GoogleIcon />}
          sx={{
            mt: 2,
            py: 1.5,
            borderColor: '#dadce0',
            color: '#3c4043',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(66, 133, 244, 0.04)',
              borderColor: '#4285f4',
            },
          }}
        >
          Googleでログイン
        </Button>

        {/* Links */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Link
              component={RouterLink}
              to="/auth/forgot-password"
              onClick={onForgotPassword}
              sx={{ textDecoration: 'none' }}
            >
              パスワードを忘れた方はこちら
            </Link>
          </Typography>

          <Typography variant="body2" color="text.secondary">
            アカウントをお持ちでない方は{' '}
            <Link
              component={RouterLink}
              to="/auth/register"
              onClick={onRegister}
              sx={{ textDecoration: 'none', fontWeight: 'medium' }}
            >
              新規登録
            </Link>
          </Typography>
        </Box>

        {/* 企業ログインへのリンク */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 3,
            pt: 3,
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
            企業の方はこちら
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={onCompanyLogin}
            sx={{ fontWeight: 500, color: '#2E5BBA' }}
          >
            企業ログイン画面へ
          </Link>
        </Box>
      </Box>

      {/* Email Verification Prompt */}
      <EmailVerificationPrompt
        open={showEmailVerification}
        email={unverifiedEmail}
        onClose={() => setShowEmailVerification(false)}
      />
    </>
  );
};

export default LoginForm;
